import { getCandidateOx } from '@/containers/execute-identification-management/utils/get-cadidate-ox';
import { ListSimpleAnimalResponseSimpleAnimalsItem } from '@/query';
import { SyncIdentificationData, SyncIdentificationDexieService } from './database-service';

const databaseService = new SyncIdentificationDexieService();

export type IdentificationSource = 'master-local' | 'slave-ws';

export interface IdentificationContext {
  oxen: ListSimpleAnimalResponseSimpleAnimalsItem[];
  gtaId: number;
  age: number;
}

export interface SubmitInput {
  payload: SyncIdentificationData;
  source: IdentificationSource;
  context: IdentificationContext | null;
}

export interface SubmitResult {
  payload: SyncIdentificationData;
  reassigned: boolean;
}

const LOG_PREFIX = '[PIPELINE]';

export const NO_ANIMAL_AVAILABLE_CODE = 'NO_ANIMAL_AVAILABLE' as const;

export class NoAnimalAvailableError extends Error {
  readonly code = NO_ANIMAL_AVAILABLE_CODE;

  constructor(message = 'Nenhum animal disponível para identificação') {
    super(message);
    this.name = 'NoAnimalAvailableError';
  }
}

let submitCounter = 0;

class IdentificationPipeline {
  private chain: Promise<unknown> = Promise.resolve();
  private inFlightOxIds = new Set<number>();

  submit(input: SubmitInput): Promise<SubmitResult> {
    const submitId = ++submitCounter;

    console.log(`${LOG_PREFIX} submit#${submitId} ENQUEUE`, {
      source: input.source,
      sisbov: input.payload.sisbov,
      oxId: input.payload.oxId,
      gtaId: input.payload.gtaId,
      hasContext: Boolean(input.context),
      oxenInContext: input.context?.oxen.length ?? 0,
      currentInFlight: Array.from(this.inFlightOxIds)
    });

    const next = this.chain.then(
      () => this.process(input, submitId),
      () => this.process(input, submitId)
    );

    this.chain = next.catch(() => undefined);

    return next;
  }

  private async process(input: SubmitInput, submitId: number): Promise<SubmitResult> {
    const { payload, context } = input;

    console.log(`${LOG_PREFIX} submit#${submitId} START process`, {
      source: input.source,
      requestedOxId: payload.oxId,
      sisbov: payload.sisbov,
      inFlight: Array.from(this.inFlightOxIds)
    });

    let finalOxId = payload.oxId;
    let reassigned = false;

    const inFlightConflict = this.inFlightOxIds.has(finalOxId);
    const dbConflict = !inFlightConflict && (await this.isOxIdInDb(finalOxId));
    const conflict = inFlightConflict || dbConflict;

    if (conflict) {
      console.warn(`${LOG_PREFIX} submit#${submitId} CONFLICT detected`, {
        requestedOxId: finalOxId,
        reason: inFlightConflict ? 'in-flight' : 'already in DB'
      });

      if (!context || context.oxen.length === 0) {
        console.error(`${LOG_PREFIX} submit#${submitId} ABORT — sem contexto para repick`);
        throw new Error('Conflito de oxId sem contexto disponível para reescolher candidato');
      }

      const filteredOxen = context.oxen.filter((item) => !this.inFlightOxIds.has(item.ox.id));

      console.log(`${LOG_PREFIX} submit#${submitId} REPICK starting`, {
        gtaId: context.gtaId,
        age: context.age,
        oxenAfterInFlightFilter: filteredOxen.length,
        oxenBefore: context.oxen.length
      });

      const newCandidate = await getCandidateOx({
        oxen: filteredOxen,
        gtaId: context.gtaId,
        age: context.age
      });

      if (!newCandidate) {
        console.error(`${LOG_PREFIX} submit#${submitId} ABORT — nenhum candidato disponível`);
        throw new NoAnimalAvailableError();
      }

      console.log(`${LOG_PREFIX} submit#${submitId} REPICK success`, {
        oldOxId: payload.oxId,
        newOxId: newCandidate.id
      });

      finalOxId = newCandidate.id;
      reassigned = true;
    }

    this.inFlightOxIds.add(finalOxId);
    console.log(`${LOG_PREFIX} submit#${submitId} RESERVED oxId=${finalOxId}`, {
      inFlightAfter: Array.from(this.inFlightOxIds)
    });

    try {
      const finalPayload: SyncIdentificationData = { ...payload, oxId: finalOxId };
      await databaseService.addPendingData(finalPayload);

      console.log(`${LOG_PREFIX} submit#${submitId} SAVED`, {
        source: input.source,
        sisbov: finalPayload.sisbov,
        finalOxId,
        reassigned
      });

      return { payload: finalPayload, reassigned };
    } catch (error) {
      console.error(`${LOG_PREFIX} submit#${submitId} SAVE FAILED`, error);
      throw error;
    } finally {
      this.inFlightOxIds.delete(finalOxId);
      console.log(`${LOG_PREFIX} submit#${submitId} RELEASED oxId=${finalOxId}`, {
        inFlightAfter: Array.from(this.inFlightOxIds)
      });
    }
  }

  private async isOxIdInDb(oxId: number): Promise<boolean> {
    const all = await databaseService.getSuccessData();
    return all.some((item) => item.oxId === oxId);
  }
}

export const identificationPipeline = new IdentificationPipeline();
